import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

const ConditionNode: React.FC<NodeProps<FlowNode['data']>> = ({ data, selected }) => {
  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-purple-400 to-purple-500 border-2 transition-all duration-200 ${
      selected ? 'border-purple-600 shadow-xl scale-105' : 'border-purple-300'
    }`}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <QuestionMarkCircleIcon className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {data.label}
          </div>
          {data.description && (
            <div className="text-xs text-purple-100 truncate mt-1">
              {data.description}
            </div>
          )}
          
          {/* Condition preview */}
          {data.config?.condition && (
            <div className="text-xs text-purple-100 mt-1 font-mono">
              {data.config.condition}
            </div>
          )}
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-500' : 'bg-green-400'
          }`} />
        </div>
      </div>

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-500 bg-opacity-20 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Execution result */}
      {data.executionData !== undefined && (
        <div className="mt-2 text-xs text-purple-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          Result: {data.executionData ? 'TRUE' : 'FALSE'}
        </div>
      )}

      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="w-3 h-3 bg-white border-2 border-purple-500"
        style={{ left: -6 }}
      />

      {/* Output handles - TRUE és FALSE ágak */}
      <Handle
        type="source"
        position={Position.Right}
        id="true"
        className="w-3 h-3 bg-green-400 border-2 border-green-600"
        style={{ right: -6, top: '30%' }}
      />
      
      <Handle
        type="source"
        position={Position.Right}
        id="false"
        className="w-3 h-3 bg-red-400 border-2 border-red-600"
        style={{ right: -6, top: '70%' }}
      />
      
      {/* Labels for outputs - kompakt betűkkel és tooltip */}
      <div 
        className="absolute -right-5 text-xs font-bold text-green-700 dark:text-green-400 pointer-events-none select-none"
        style={{ top: 'calc(30% - 8px)' }}
        title="TRUE"
      >
        T
      </div>
      <div 
        className="absolute -right-5 text-xs font-bold text-red-700 dark:text-red-400 pointer-events-none select-none"
        style={{ top: 'calc(70% - 8px)' }}
        title="FALSE"
      >
        F
      </div>
    </div>
  );
};

export default ConditionNode;
